import { z } from 'zod';

// The five steps the storefront funnel is measured in, ordered from widest to narrowest.
export const STOREFRONT_EVENT_NAMES = [
  'page_view',
  'product_view',
  'add_to_cart',
  'begin_checkout',
  'purchase'
] as const;

export type StorefrontEventName = (typeof STOREFRONT_EVENT_NAMES)[number];

// Inactivity that ends a session; a convention rather than a truth, since widening it merges two visits into one and moves every session-denominated rate.
export const SESSION_IDLE_MS = 30 * 60 * 1000;

// Names the UTC day an instant falls in; midnight UTC is the only other session terminator, so a visit never straddles two report days.
export const utcDayKey = (at: number): string => new Date(at).toISOString().slice(0, 10);

// Most events one beacon may carry; the client flushes well before this and the server rejects past it.
export const MAX_EVENTS_PER_BATCH = 50;

// How far ahead of the server clock a client timestamp may sit before it is discarded.
export const MAX_CLOCK_SKEW_MS = 2 * 60 * 1000;

// How far behind the server clock a client timestamp may sit; older than this is not a live visit.
export const MAX_EVENT_AGE_MS = 24 * 60 * 60 * 1000;

// One product in an event, as the ad platforms list them: its id, how many, and the price of one.
export const StorefrontEventContentSchema = z.object({
  id: z.string().min(1).max(64),
  quantity: z.number().int().positive().max(999),
  itemPrice: z.number().nonnegative().max(1e9).optional()
});

export type StorefrontEventContent = z.infer<typeof StorefrontEventContentSchema>;

// One storefront interaction; ids are the tenant's own uuids and are verified server-side. The ad fields
// are present only when the shopper allows ad tags, and eventId is the one the browser pixel fired with,
// so the Conversions API's copy of the event is counted once.
export const StorefrontEventSchema = z.object({
  name: z.enum(STOREFRONT_EVENT_NAMES),
  ts: z.number().int().nonnegative(),
  path: z.string().max(512).optional(),
  referrer: z.string().max(1024).optional(),
  productId: z.string().uuid().optional(),
  orderId: z.string().uuid().optional(),
  utmSource: z.string().max(100).optional(),
  utmMedium: z.string().max(100).optional(),
  utmCampaign: z.string().max(100).optional(),
  eventId: z.string().max(64).optional(),
  value: z.number().nonnegative().max(1e9).optional(),
  currency: z.string().length(3).optional(),
  contentName: z.string().max(200).optional(),
  contents: z.array(StorefrontEventContentSchema).max(50).optional()
});

// One beacon payload; the server appends the UTC day to sessionKey before storing, so the midnight boundary holds whatever the client does.
export const StorefrontEventBatchSchema = z.object({
  sessionKey: z.string().regex(/^[a-z0-9]{16,48}$/, 'sessionKey must be 16-48 lowercase alphanumerics'),
  sessionStartedAt: z.number().int().nonnegative(),
  // The browser's own ad id, the one its pixel matches on; sent only when the shopper allows ad tags.
  visitorId: z.string().uuid().optional(),
  events: z.array(StorefrontEventSchema).min(1).max(MAX_EVENTS_PER_BATCH)
});

export type StorefrontEvent = z.infer<typeof StorefrontEventSchema>;
export type StorefrontEventBatch = z.infer<typeof StorefrontEventBatchSchema>;

// What the ingest endpoint reports back; the storefront client never reads it, tests and curl do.
export interface StorefrontIngestResult {
  accepted: number;
  rejected: number;
}
